import React, { useState, useContext, useEffect } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, School, CheckCircle, Clock } from 'lucide-react';

const PMPSchoolsList = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    try {
      const response = await axios.get(`${API}/pmp-schools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInspections(response.data);
    } catch (error) {
      toast.error('Failed to load PMP-Schools inspections');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Completed': 'bg-green-100 text-green-800',
      'In Progress': 'bg-blue-100 text-blue-800',
      'Pending': 'bg-yellow-100 text-yellow-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p>Loading PMP-Schools inspections...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <School className="h-6 w-6 text-blue-600" />
          <h2 className="text-2xl font-bold">PMP - Schools</h2>
        </div>
        <Button onClick={() => navigate('/field/pmp-schools/new')} data-testid="new-pmp-schools-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Inspection
        </Button>
      </div>

      {inspections.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <School className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500 mb-4">No PMP-Schools inspections found</p>
            <Button onClick={() => navigate('/field/pmp-schools/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Inspection
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {inspections.map((inspection) => (
            <Card key={inspection.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/field/pmp-schools/${inspection.id}`)}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">{inspection.school_name}</CardTitle>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                      <span><strong>Address:</strong> {inspection.address}</span>
                      <span>•</span>
                      <span><strong>Date:</strong> {_fmtDate(inspection.inspection_date)}</span>
                    </div>
                  </div>
                  <Badge className={getStatusColor(inspection.status || 'Completed')}>
                    {inspection.status || 'Completed'}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-600">
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    <div>
                      <span className="text-gray-500">Inspector:</span>
                      <span className="ml-2 font-medium">{inspection.inspector_name}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">School Type:</span>
                      <span className="ml-2 font-medium capitalize">{inspection.school_type}</span>
                    </div>
                  </div>
                  {inspection.overall_notes && (
                    <p className="text-xs text-gray-500 mt-2">
                      {inspection.overall_notes.substring(0, 100)}{inspection.overall_notes.length > 100 ? '...' : ''}
                    </p>
                  )}
                  <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                    {inspection.photos && inspection.photos.length > 0 && (
                      <span><strong>Photos:</strong> {inspection.photos.length}</span>
                    )}
                    {inspection.signature && (
                      <span className="flex items-center">
                        <CheckCircle className="h-3 w-3 mr-1 text-green-600" />
                        Signed
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PMPSchoolsList;
